"use client";

import * as React from "react";
import { Check, Copy, FileText, Route, Truck, Wallet } from "lucide-react";
import { toast } from "sonner";
import type { Ciot, Cte, Evento, Mdfe } from "@/types";
import { useApiQuery } from "@/lib/query";
import { fmt } from "@/lib/format";
import { resolvePeriodo } from "@/lib/periodo";
import { STATUS_CTE, STATUS_MDFE, type StatusDef, statusCiot } from "@/lib/status";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/status";
import { Timeline } from "@/components/timeline";
import { useCte, useCtes } from "@/features/cte/api";
import { useCteActions } from "@/features/cte/components";
import { useMdfe, useMdfes } from "@/features/mdfe/api";
import { useMdfeActions } from "@/features/mdfe/actions";
import { useCiotActions } from "@/features/ciot/ciot-screen";
import { useNav } from "../nav";
import { Card, Chips, KV, List, ListSkeleton, MEmpty, Page, Row, SearchField, Section, useInfinite } from "../ui";
import { ActionFooter } from "../actions";

type Tipo = "cte" | "mdfe" | "ciot";

function norm(s: string) {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

const mdfeStatus = (m: Mdfe): StatusDef => (m.situacao === "aberto" && m.horasAberto > 24 ? { label: "Aberto > 24 h", tom: "warning" } : STATUS_MDFE[m.situacao]);
const ciotStatus = (c: Ciot): StatusDef => statusCiot(c);

export function MDocumentos({ params }: { params: Record<string, string> }) {
  const [tipo, setTipo] = React.useState<Tipo>((params.tipo as Tipo) ?? "mdfe");
  const [busca, setBusca] = React.useState("");
  return (
    <Page title="Documentos">
      <div className="sticky top-0 z-10 space-y-2.5 bg-app px-4 pt-3 pb-2">
        <div role="tablist" className="grid h-10 grid-cols-3 rounded-xl border border-border bg-surface-2 p-1">
          {(
            [
              ["mdfe", "MDF-e"],
              ["cte", "CT-e"],
              ["ciot", "CIOT"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={tipo === v}
              onClick={() => setTipo(v)}
              className={cn("rounded-lg text-sm font-medium transition-colors duration-100", tipo === v ? "bg-surface text-fg shadow-popover" : "text-muted")}
            >
              {l}
            </button>
          ))}
        </div>
        <SearchField value={busca} onChange={setBusca} placeholder={tipo === "cte" ? "Número, tomador, chave" : tipo === "mdfe" ? "Número, placa" : "Número, TAC"} />
      </div>
      {tipo === "mdfe" ? <ListaMdfe busca={busca} /> : tipo === "cte" ? <ListaCte busca={busca} /> : <ListaCiot busca={busca} />}
    </Page>
  );
}

function ListaMdfe({ busca }: { busca: string }) {
  const [f, setF] = React.useState<"aberto" | "atrasado" | "encerrado" | "todos">("aberto");
  const q = useMdfes("todos");
  const d = q.data ?? [];
  const rows = d
    .filter((m) => (f === "todos" ? true : f === "atrasado" ? m.situacao === "aberto" && m.horasAberto > 24 : m.situacao === f))
    .filter((m) => !busca || norm(`${m.numero} ${m.placa} ${m.condutores.map((c) => c.nome).join(" ")}`).includes(norm(busca)));
  const { visiveis, sentinel } = useInfinite(q.data ? rows : undefined);
  const atrasados = d.filter((m) => m.situacao === "aberto" && m.horasAberto > 24).length;
  return (
    <div className="px-4 pb-6">
      <Chips
        value={f}
        onChange={setF}
        options={[
          { value: "aberto", label: "Em aberto", count: d.filter((m) => m.situacao === "aberto").length },
          { value: "atrasado", label: "> 24 h", count: atrasados, tom: atrasados ? "warning" : undefined },
          { value: "encerrado", label: "Encerrados" },
          { value: "todos", label: "Todos" },
        ]}
      />
      <div className="mt-2.5">
        {!q.data ? (
          <ListSkeleton />
        ) : !rows.length ? (
          <MEmpty icon={Truck} text="Nenhum MDF-e" />
        ) : (
          <List>
            {visiveis?.map((m) => (
              <Row
                key={m.id}
                title={`MDF-e ${fmt.int(m.numero)} · ${m.placa}`}
                subtitle={`${[m.ufInicio, ...m.percurso, m.ufFim].join(" → ")}${m.situacao === "aberto" ? ` · há ${fmt.horas(m.horasAberto)}` : ""}`}
                trailing={<StatusBadge def={mdfeStatus(m)} />}
                onClick={() => useNav.getState().push("mdfe", { id: m.id })}
              />
            ))}
          </List>
        )}
        {sentinel}
      </div>
    </div>
  );
}

function ListaCte({ busca }: { busca: string }) {
  const [f, setF] = React.useState<"todos" | "autorizado" | "rejeitado" | "cancelado">("todos");
  const q = useCtes(resolvePeriodo("30d"));
  const d = q.data ?? [];
  const rows = d
    .filter((c) => f === "todos" || c.situacao === f)
    .filter((c) => !busca || norm(`${c.numero} ${c.tomadorNome} ${c.chave}`).includes(norm(busca).replace(/\s/g, "")) || norm(`${c.numero} ${c.tomadorNome}`).includes(norm(busca)));
  const { visiveis, sentinel } = useInfinite(q.data ? rows : undefined);
  const rej = d.filter((c) => c.situacao === "rejeitado").length;
  return (
    <div className="px-4 pb-6">
      <Chips
        value={f}
        onChange={setF}
        options={[
          { value: "todos", label: "Todos", count: d.length },
          { value: "autorizado", label: "Autorizados" },
          { value: "rejeitado", label: "Rejeitados", count: rej, tom: rej ? "danger" : undefined },
          { value: "cancelado", label: "Cancelados" },
        ]}
      />
      <div className="mt-2.5">
        {!q.data ? (
          <ListSkeleton />
        ) : !rows.length ? (
          <MEmpty icon={FileText} text="Nenhum CT-e" />
        ) : (
          <List>
            {visiveis?.map((c) => (
              <Row
                key={c.id}
                title={`CT-e ${fmt.int(c.numero)} · ${fmt.money(c.valor)}`}
                subtitle={`${c.tomadorNome} · ${fmt.dayShort(c.emissao)}`}
                trailing={<StatusBadge def={STATUS_CTE[c.situacao]} />}
                onClick={() => useNav.getState().push("cte", { id: c.id })}
              />
            ))}
          </List>
        )}
        {sentinel}
      </div>
    </div>
  );
}

function ListaCiot({ busca }: { busca: string }) {
  const [f, setF] = React.useState<"gerado" | "quitado" | "todos">("gerado");
  const q = useApiQuery<Ciot[]>(["ciots", "todos"], "/ciots");
  const d = q.data ?? [];
  const rows = d.filter((c) => f === "todos" || c.situacao === f).filter((c) => !busca || norm(`${c.numero} ${c.tacNome}`).includes(norm(busca)));
  const { visiveis, sentinel } = useInfinite(q.data ? rows : undefined);
  return (
    <div className="px-4 pb-6">
      <Chips
        value={f}
        onChange={setF}
        options={[
          { value: "gerado", label: "A quitar", count: d.filter((c) => c.situacao === "gerado").length },
          { value: "quitado", label: "Quitados" },
          { value: "todos", label: "Todos", count: d.length },
        ]}
      />
      <div className="mt-2.5">
        {!q.data ? (
          <ListSkeleton />
        ) : !rows.length ? (
          <MEmpty icon={Wallet} text="Nenhum CIOT" />
        ) : (
          <List>
            {visiveis?.map((c) => (
              <Row
                key={c.id}
                title={c.tacNome}
                subtitle={`CIOT ${c.numero || "—"} · ${c.situacao === "gerado" ? `falta ${fmt.money(c.saldoRestante)}` : `saldo ${fmt.money(c.saldo)}`}`}
                trailing={<StatusBadge def={ciotStatus(c)} />}
                onClick={() => useNav.getState().push("ciot", { id: c.id })}
              />
            ))}
          </List>
        )}
        {sentinel}
      </div>
    </div>
  );
}

/* ---------- Detalhes ---------- */

function ChaveCard({ chave }: { chave: string }) {
  const [ok, setOk] = React.useState(false);
  return (
    <Card
      onClick={() =>
        navigator.clipboard
          ?.writeText(chave)
          .then(() => {
            setOk(true);
            toast.success("Chave copiada");
            setTimeout(() => setOk(false), 1500);
          })
          .catch(() => toast.error("Não foi possível copiar"))
      }
      className="flex items-center gap-3 p-4"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-muted">Chave de acesso</span>
        <span className="mt-0.5 block font-mono text-[13px] leading-5 break-all tnum">{fmt.chave(chave)}</span>
      </span>
      {ok ? <Check className="size-5 text-success" /> : <Copy className="size-5 text-muted" />}
    </Card>
  );
}

function Hero({ titulo, status, linhas }: { titulo: string; status: StatusDef; linhas: { label: string; value: React.ReactNode }[] }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xl leading-7 font-semibold tnum">{titulo}</span>
        <StatusBadge def={status} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3">
        {linhas.map((l) => (
          <div key={l.label} className="min-w-0">
            <p className="text-xs text-muted">{l.label}</p>
            <p className="truncate text-[15px] font-semibold tnum">{l.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ViagemLink({ id, numero }: { id: string; numero: number }) {
  return (
    <List>
      <Row icon={Route} title={`Viagem ${fmt.int(numero)}`} subtitle="Ver viagem completa" onClick={() => useNav.getState().push("viagem", { id })} />
    </List>
  );
}

export function MCte({ params }: { params: Record<string, string> }) {
  const q = useCte(params.id);
  const d = q.data;
  const c: Cte | undefined = d?.cte;
  const acoes = useCteActions(c ? [c] : []);
  return (
    <Page back title={c ? `CT-e ${fmt.int(c.numero)}` : "CT-e"} subtitle={c ? fmt.dateTime(c.emissao) : undefined} onRefresh={() => q.refetch()} footer={c ? <ActionFooter acoes={acoes.toolbar} permitidas={["dacte", "xml", "email", "cce", "cancelar"]} /> : undefined}>
      {!c || !d ? (
        <div className="p-4">
          <ListSkeleton rows={4} tall />
        </div>
      ) : (
        <div className="pb-6">
          <Section>
            <Hero
              titulo={fmt.money(c.valor)}
              status={STATUS_CTE[c.situacao]}
              linhas={[
                { label: "ICMS", value: fmt.money(c.icms) },
                { label: "IBS/CBS", value: fmt.money(c.ibsCbs) },
                { label: "CFOP", value: c.cfop },
              ]}
            />
            {c.motivo ? <p className="mt-2.5 rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger">{c.motivo}</p> : null}
          </Section>
          <Section>
            <ChaveCard chave={c.chave} />
          </Section>
          <Section title="Partes">
            <KV
              items={[
                { label: "Tomador", value: c.tomadorNome },
                { label: "Remetente", value: c.remetenteNome },
                { label: "Destinatário", value: c.destinatarioNome },
                { label: "Prestação", value: `${c.origem.uf} → ${c.destino.uf}` },
              ]}
            />
          </Section>
          <Section title="Viagem">
            <ViagemLink id={c.viagemId} numero={c.viagemNumero} />
          </Section>
          <Section title="Eventos">
            <Card className="py-1">
              <Timeline eventos={d.eventos} compact />
            </Card>
          </Section>
        </div>
      )}
      {acoes.dialogs}
    </Page>
  );
}

export function MMdfe({ params }: { params: Record<string, string> }) {
  const q = useMdfe(params.id);
  const m = q.data?.mdfe;
  const acoes = useMdfeActions(m);
  return (
    <Page back title={m ? `MDF-e ${fmt.int(m.numero)}` : "MDF-e"} subtitle={m ? fmt.dateTime(m.emissao) : undefined} onRefresh={() => q.refetch()} footer={m ? <ActionFooter acoes={acoes.toolbar} permitidas={["encerrar", "condutor", "damdfe", "cancelar"]} /> : undefined}>
      {!m ? (
        <div className="p-4">
          <ListSkeleton rows={4} tall />
        </div>
      ) : (
        <div className="pb-6">
          <Section>
            <Hero
              titulo={m.placa}
              status={mdfeStatus(m)}
              linhas={[
                { label: "Carga", value: fmt.moneyShort(m.valorCarga) },
                { label: "Peso", value: `${fmt.dec(m.pesoKg / 1000)} t` },
                m.situacao === "aberto" ? { label: "Em aberto", value: fmt.horas(m.horasAberto) } : { label: "CT-e", value: m.qtdCte },
              ]}
            />
          </Section>
          <Section>
            <ChaveCard chave={m.chave} />
          </Section>
          <Section title="Percurso">
            <Card className="flex flex-wrap items-center gap-1.5 p-4">
              {[m.ufInicio, ...m.percurso, m.ufFim].map((uf, i, a) => (
                <React.Fragment key={`${uf}-${i}`}>
                  {i > 0 ? <span className="h-px w-4 bg-border-strong" /> : null}
                  <span className={cn("inline-flex h-8 items-center rounded-lg border px-2.5 text-sm", i === 0 || i === a.length - 1 ? "border-border-strong bg-surface-2 font-semibold" : "border-border text-muted")}>{uf}</span>
                </React.Fragment>
              ))}
            </Card>
          </Section>
          <Section title="Condutores">
            <List>
              {m.condutores.map((c) => (
                <Row key={c.cpf} title={c.nome} subtitle={fmt.cpf(c.cpf)} />
              ))}
            </List>
          </Section>
          {m.encerramento ? (
            <Section title="Encerramento">
              <KV
                items={[
                  { label: "Data", value: fmt.dateTime(m.encerramento.data) },
                  { label: "Local", value: `${m.encerramento.municipio}/${m.encerramento.uf}` },
                ]}
              />
            </Section>
          ) : null}
          <Section title="Viagem">
            <ViagemLink id={m.viagemId} numero={m.viagemNumero} />
          </Section>
          <Section title="Eventos">
            <Card className="py-1">
              <Timeline eventos={q.data?.eventos} compact />
            </Card>
          </Section>
        </div>
      )}
      {acoes.dialogs}
    </Page>
  );
}

export function MCiot({ params }: { params: Record<string, string> }) {
  const q = useApiQuery<{ ciot: Ciot; eventos: Evento[] }>(["ciots", "item", params.id], params.id ? `/ciots/${params.id}` : null);
  const c = q.data?.ciot;
  const acoes = useCiotActions(c);
  return (
    <Page back title={c ? `CIOT ${c.numero || ""}` : "CIOT"} subtitle={c ? c.tacNome : undefined} onRefresh={() => q.refetch()} footer={c ? <ActionFooter acoes={acoes.toolbar} permitidas={["quitar", "cancelar"]} /> : undefined}>
      {!c ? (
        <div className="p-4">
          <ListSkeleton rows={4} tall />
        </div>
      ) : (
        <div className="pb-6">
          <Section>
            <Hero
              titulo={fmt.money(c.situacao === "gerado" ? c.saldoRestante : c.saldo)}
              status={ciotStatus(c)}
              linhas={[
                { label: "Contratado", value: fmt.moneyShort(c.valor) },
                { label: "Adiantam.", value: fmt.moneyShort(c.adiantamento) },
                { label: "Líquido", value: fmt.moneyShort(c.liquido) },
              ]}
            />
          </Section>
          <Section title="TAC">
            <KV
              items={[
                { label: "Nome", value: c.tacNome },
                { label: "CPF", value: fmt.cpf(c.tacCpf) },
                { label: "Pagamento", value: { pix: "Pix", conta: "Conta bancária", cartao: "Cartão IPEF" }[c.formaPagamento] },
                { label: "Operadora", value: c.ipef },
              ]}
            />
          </Section>
          <Section title="Retenções">
            <KV
              items={[
                { label: "INSS", value: fmt.money(c.inss) },
                { label: "SEST/SENAT", value: fmt.money(c.sestSenat) },
                { label: "IRRF", value: fmt.money(c.irrf) },
              ]}
            />
          </Section>
          {c.pagamentos.length ? (
            <Section title="Pagamentos">
              <List>
                {c.pagamentos.map((p) => (
                  <Row key={p.id} title={p.tipo === "adiantamento" ? "Adiantamento" : "Saldo"} subtitle={`${fmt.date(p.data)} · ${p.forma.toUpperCase()}`} trailing={<span className="text-[15px] font-semibold tnum">{fmt.money(p.valor)}</span>} />
                ))}
              </List>
            </Section>
          ) : null}
          <Section title="Viagem">
            <ViagemLink id={c.viagemId} numero={c.viagemNumero} />
          </Section>
        </div>
      )}
      {acoes.dialogs}
    </Page>
  );
}
